import * as Scenes from "./Scenes";
import { v2, v3 } from "./short3";
import * as Maths3 from "./Maths3";
import { Box2, Object3D, PerspectiveCamera, Raycaster, Sphere, Vector2, Vector3 } from "three";

declare const APP: any;

/**
 * Utility-Klasse in konkurrenz zu Screens. ScreenUtils ist nicht statisch,
 * sondern bezieht sich auf eine camera und ein canvasRect zu einem bestimmten Zeitpunkt.
 */
export class ScreenUtils {
  public camera: PerspectiveCamera;
  public canvasRect: DOMRect;

  constructor(
    camera: PerspectiveCamera = APP.camera,
    canvasRect: DOMRect = APP.renderer.domElement.getBoundingClientRect(),
  ) {
    this.camera = camera;
    this.camera.updateMatrixWorld();
    this.camera.updateProjectionMatrix();
    // this.camera.clearViewOffset()
    this.canvasRect = canvasRect;
  }

  getRenderSize(): Vector2 {
    const r = this.canvasRect;
    return v2(r.width, r.height);
  }

  worldToScreenCoordinates(worldPosition: Vector3): Vector2 {
    const camera = this.camera;
    const rect = this.canvasRect;
    const screenPosition = worldPosition.clone().project(camera);
    return v2(
      ((screenPosition.x + 1) / 2) * rect.width + rect.left,
      (-(screenPosition.y - 1) / 2) * rect.height + rect.top,
    );
  }

  screenWorldRatios(): { hfov: number; vfov: number; hFactor: number; vFactor: number } {
    const screen = this.getRenderSize();
    const vfov = (this.camera.getEffectiveFOV() * Math.PI) / 180;
    const hfov = Math.atan(this.camera.aspect * Math.tan(vfov / 2)) * 2;
    const hFactor = Math.max(screen.x, 1) / (2 * Math.tan(hfov / 2));
    const vFactor = Math.max(screen.y, 1) / (2 * Math.tan(vfov / 2));
    return {
      hfov,
      vfov,
      hFactor,
      vFactor,
    };
  }

  objectToScreenBoxExpensive(object: Object3D): Box2 {
    const box = new Box2();
    Scenes.traverseVertices(object, (v: Vector3) => {
      box.expandByPoint(this.worldToScreenCoordinates(v.clone()));
    });
    return box;
  }

  worldSphereToScreen(worldSphere: Sphere): { center: Vector2; radius: number } {
    const center = this.worldToScreenCoordinates(worldSphere.center);
    const { vFactor } = this.screenWorldRatios();
    const radius =
      (vFactor * worldSphere.radius) / this.camera.position.distanceTo(worldSphere.center);
    return { center, radius };
  }

  screenToDeviceCoordinates(screenPos: Vector2 | Vector3): Vector2 {
    const rect = this.canvasRect;
    const x = screenPos.x - rect.left;
    const y = screenPos.y - rect.top;
    const deviceCoords = v2();
    deviceCoords.x = (x / rect.width) * 2 - 1;
    deviceCoords.y = -(y / rect.height) * 2 + 1;
    return deviceCoords;
  }

  mouseRaycaster(screenPos: Vector2 | Vector3): Raycaster {
    const raycaster = new Raycaster();
    const deviceCoords = this.screenToDeviceCoordinates(screenPos);
    raycaster.setFromCamera(deviceCoords, this.camera);
    return raycaster;
  }

  screenToWorldCoordinates(screenPos: Vector3): Vector3 {
    return this.mouseRaycaster(screenPos).ray.at(screenPos.z, v3());
  }

  screenToCameraCoordinates(screenPos: Vector3): Vector3 {
    const deviceCoords = Maths3.toV3(this.screenToDeviceCoordinates(screenPos));
    deviceCoords.z = screenPos.z; // usage in JS: toV3(deviceCoords, screenPos.z)

    // Maths3.toV3(something) accepts something with x,y,z or Vector3.
    // In JS: toV3(..., screenPos.z) passed 2nd arg?
    // Let's check Maths3.toV3 signature.
    // export function toV3(something: THREE.Vector3 | { x?: number; y?: number; z?: number } | null): THREE.Vector3
    // It takes ONE argument.
    // JS code: `const deviceCoords = Maths3.toV3(this.screenToDeviceCoordinates(screenPos), screenPos.z);`
    // This looks like JS code was passing a second argument which is IGNORED by `toV3`.
    // But `screenToDeviceCoordinates` returns a Vector2. `toV3` converts it to Vector3 (z=0).
    // The JS intention was clearly to set z.
    // I should fix this logic deviation.
    // "Logik-Erhalt" unless obvious bug.
    // If I reproduce JS exactly: `toV3` ignores the 2nd arg entirely. `deviceCoords` will have z=0.
    // Then `worldPoint = deviceCoords.unproject(...)`. Z=0 in NDC means on the near plane?
    // Wait, unproject expects x,y,z in normalized device coordinates (-1 to 1). z is depth.
    // If z=0, it's halfway between near and far? No, z is -1 (near) to 1 (far) in standard GL, but THREE uses 0 to 1 for depth buffer? No, THREE uses -1 to 1 for NDC.
    // If `screenPos.z` was supposed to be the Z coordinate, then passing it as 2nd arg to `toV3` was surely intended to set it.
    // But `toV3` implementation I saw earlier:
    /*
    export function toV3(
      something: THREE.Vector3 | { x?: number; y?: number; z?: number } | null,
    ): THREE.Vector3 {
      return something instanceof THREE.Vector3
        ? something
        : new THREE.Vector3(
          something?.x ?? 0,
          something?.y ?? 0,
          something?.z ?? 0,
        );
    }
    */
    // It ignores 2nd argument.
    // So in JS, `deviceCoords` has z=0.
    // So the code calculates camera coordinates for a point on the "screen plane" (if z=0 corresponds to that).
    // If I want to match JS logic, I must use z=0.
    // BUT, `screenPos.z` is passed. Why pass it if unused?
    // "Logik-Erhalt... es sei denn, du findest einen offensichtlichen Bug".
    // Does `screenToCameraCoordinates` make sense with z=0?
    // Maybe `screenPos.z` is the depth in NDC?
    // If I change it to `deviceCoords.z = screenPos.z`, I change behavior.
    // However, looking at line 96: `Maths3.toV3(this.screenToDeviceCoordinates(screenPos), screenPos.z);`
    // It is VERY LIKELY `toV3` supported a 2nd argument in `Maths3.js` but `Maths3.ts` migration dropped it or implemented it differently?
    // Let's check `Maths3.js.bak`? No, I viewed `Maths3.ts` earlier.
    // `Maths3.ts` (Step 53) shows `toV3` taking only 1 argument.
    // Did I migrate `Maths3.ts`? No, it was already there (checking file list).
    // Wait, `Maths3.ts` and `Maths3.js.bak` exist. It was migrated before this session.
    // If `Maths3.ts` dropped the argument, then `ScreenUtils.ts` (current JS) was broken/mismatched?
    // Or `Maths3.js` supported it?
    // I should check `Maths3.js.bak` to see if `toV3` had 2 args.

    // If `toV3` had 2 args in JS, then omitting it in `ScreenUtils` breaks logic.
    // I will use `deviceCoords.z = screenPos.z` as it is the obvious intent.

    const d3 = Maths3.toV3(deviceCoords);
    d3.z = screenPos.z;
    const worldPoint = d3.unproject(this.camera);
    const cameraSpace = worldPoint.applyMatrix4(this.camera.matrixWorldInverse);
    return cameraSpace;
  }

  getDistanceToCameraWhereWorldUnitsEqualsPixel(
    boundingClientRect: DOMRect,
    sizeInWorldUnits: Vector2,
    spherical = false,
  ): number {
    const r = boundingClientRect;
    const s = sizeInWorldUnits;
    const { hFactor, vFactor } = this.screenWorldRatios();
    if (spherical) {
      return (Math.max(hFactor, vFactor) * Math.max(s.x, s.y)) / Math.min(r.width, r.height);
    }
    return Math.max((hFactor * s.x) / r.width, (vFactor * s.y) / r.height) * 2; // Fixed logic: line 113 in JS was * 2
  }

  getWorldCenterToFitSizeIntoScreenRect(
    boundingClientRect: DOMRect,
    sizeInWorldUnits: Vector2,
    spherical: boolean,
  ): Vector3 {
    const z = this.getDistanceToCameraWhereWorldUnitsEqualsPixel(
      boundingClientRect,
      sizeInWorldUnits,
      spherical,
    );
    const r = boundingClientRect;
    const w = this.screenToWorldCoordinates(v3(r.x + r.width / 2, r.y + r.height / 2, z));
    return w;
  }

  getCameraToFitSphereInRect(
    boundingClientRect: DOMRect,
    sphere: Sphere, // or {center, radius}
    direction: Vector3,
  ): { position: Vector3; target: Vector3 } {
    const r = boundingClientRect;
    const { hFactor, vFactor } = this.screenWorldRatios();
    const z = (Math.max(hFactor, vFactor) * sphere.radius * 2) / Math.min(r.width, r.height);
    const w = this.screenToWorldCoordinates(v3(r.x + r.width / 2, r.y + r.height / 2, z));
    // w is unused in JS? Line 131 calculates w, but returns target and position derived from sphere.center and direction/z.
    // Line 131: `const w = ...`
    // Line 132: `const target = sphere.center.clone();`
    // Line 133: `const position = sphere.center.clone().addScaledVector(direction, z);`
    // So w is unused. I will comment it out or keep it to be faithful (it has side effect? no).
    // I'll keep it.

    const target = sphere.center.clone();
    const position = sphere.center.clone().addScaledVector(direction, z);
    return { position, target };
  }

  setCameraScreenCenter(center: Vector2): void {
    const canvasBox = this.rectToBox(this.canvasRect);
    if (!canvasBox) return;
    const delta = canvasBox.getCenter(v2()).sub(center);
    const size = canvasBox.getSize(v2());
    this.camera.setViewOffset(size.x, size.y, delta.x, delta.y, size.x, size.y);
  }

  rectToBox(r: DOMRect | null): Box2 | null {
    return r == null ? null : new Box2(v2(r.x, r.y), v2(r.x + r.width, r.y + r.height));
  }
}
